import styled from '@emotion/styled';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Radio, { radioClasses } from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { ChangeEvent, FunctionComponent, ReactElement, useMemo } from 'react';

import { PaletteColor } from '../../styling/AppStyles';
import ExinMuiFormControlLabel from './ExinMuiFormControlLabel';
import ExinMuiIconButton from './ExinMuiIconButton';
import ExinMuiTooltip from './ExinMuiTooltip';

/**
 * A MUI <Radio> with custom styles
 */
const ExinMuiRadio = styled(Radio)`
  color: ${PaletteColor.text};
  &.${radioClasses.checked} {
    color: ${PaletteColor.text};
  }
`;

/**
 * A convenience wrapper for MUI <RadioGroup> -- uses an array of `options`, and does all the
 * mapping to styled child components for you. Assumes that the value is always "controlled".
 */
const ExinMuiRadioGroup: FunctionComponent<{
  options: Array<{
    value: string;
    label: string | ReactElement;
    tooltip?: string | ReactElement;
    disabled?: boolean;
  }>;
  value: string | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}> = ({ options, value, onChange }) => {
  const optionElements = useMemo(
    () =>
      options.map((option) => (
        <div key={option.value}>
          <ExinMuiFormControlLabel
            value={option.value}
            control={<ExinMuiRadio />}
            label={option.label}
            disabled={option.disabled}
          />
          {option.tooltip && (
            <ExinMuiTooltip title={option.tooltip} placement="right-start">
              <ExinMuiIconButton>
                <HelpOutline />
              </ExinMuiIconButton>
            </ExinMuiTooltip>
          )}
        </div>
      )),
    [options]
  );
  return (
    <RadioGroup value={value || ''} onChange={onChange}>
      {optionElements}
    </RadioGroup>
  );
};

export default ExinMuiRadioGroup;
